import React, {
    useContext,
    useState
}
from "react";

import {
    OMNISCAN_BLUE
}
from "../../constants/colors";

import Header
from "../../components/Header";

import LeftMenu
from "../../components/LeftMenu";

import useSnippetDocumentBuilder
from "./hooks/useSnippetDocumentBuilder";

import DocumentCanvas
from "./components/DocumentCanvas";

import DocumentBasket
from "./components/DocumentBasket";

import {
    store
}
from "../../context/store.js";

import {
    CHANGE_DOCUMENT_BASKET
}
from "../../constants/action-types";

import {
    JWT
}
from "../../constants/local-storage";


//========================================================
// Snippet Document Builder
//
// Builds a document from:
//
// - Source basket content
// - Edited copies of source text
// - Authored text
// - Uploaded images
// - Derived images
//
// Source provenance is retained in the document JSON.
//
// Binary images are attached separately using FormData.
//========================================================

export default function SnippetDocumentBuilder({

    onClose

})
{

    //========================================================
    // Global State
    //========================================================

    const globalState =
        useContext(store);

    const basketItems =
        globalState?.state?.documentBasket || [];


    //========================================================
    // Document Builder
    //========================================================

    const {

        documentLayout,

        addPage,

        deletePage,

        addRow,

        deleteRow,

        moveRow,

        changeRowLayout,

        setCellContent,

        clearCellContent,

        updateCellContent,

        setPageOrientation,

        setDocumentTitle

    } = useSnippetDocumentBuilder();


    //========================================================
    // Document Creation State
    //========================================================

    const [
        creatingDocument,
        setCreatingDocument
    ] = useState(false);

    const [
        documentCreationError,
        setDocumentCreationError
    ] = useState(null);


    //========================================================
    // Add Merged Image To Basket
    //========================================================

    const addMergedImageToBasket = (mergedImage) =>
    {

        //====================================================
        // Validate
        //====================================================

        if (

            !mergedImage ||
            !mergedImage.basketId ||
            !mergedImage.blob

        )
        {

            console.error(

                "Unable to add merged image to basket.",

                mergedImage

            );

            return;

        }


        //====================================================
        // Duplicate Protection
        //====================================================

        const alreadyExists =
            basketItems.some(

                item =>
                    String(item.basketId) ===
                    String(mergedImage.basketId)

            );

        if (
            alreadyExists
        )
        {
            return;
        }


        //====================================================
        // Derived Basket Item
        //====================================================

        const basketItem =
        {

            ...mergedImage,

            basketContentType:
                "DERIVED_IMAGE",

            sectionType:
                "IMAGE",

            contentType:
                "image/png",

            selectionType:
                "DERIVED",

            source:
                "IMAGE_COMPOSER",

            selectedText:
                null

        };


        //====================================================
        // Update Global Basket
        //====================================================

        const currentBasket =
            globalState?.state?.documentBasket || [];

        globalState.dispatch(

            {

                type:
                    CHANGE_DOCUMENT_BASKET,

                value:
                    [
                        ...currentBasket,
                        basketItem
                    ]

            }

        );

    };


    //========================================================
    // Basket Usage
    //
    // Derived directly from the working document layout.
    //========================================================

    const basketUsage =
        {};

    documentLayout?.pages?.forEach(

        page =>
        {

            page?.rows?.forEach(

                row =>
                {

                    row?.cells?.forEach(

                        cell =>
                        {

                            const basketId =
                                cell?.content?.basketId;

                            if (
                                basketId == null
                            )
                            {
                                return;
                            }

                            basketUsage[basketId] =
                                (
                                    basketUsage[basketId] || 0
                                ) + 1;

                        }

                    );

                }

            );

        }

    );


    //========================================================
    // Find Basket Item
    //========================================================

    const findBasketItem = (basketId) =>
    {

        if (
            basketId == null
        )
        {
            return null;
        }

        return (

            basketItems.find(

                item =>
                    String(item.basketId) ===
                    String(basketId)

            )

            ||

            null

        );

    };


    //========================================================
    // Build Source Content
    //
    // Preserves the source document, snippet, section,
    // geometry and derived-image provenance.
    //
    // Browser Blobs and Files are never included in JSON.
    //========================================================

    const buildSourceContent = (basketItem) =>
    {

        if (
            !basketItem
        )
        {
            return null;
        }


        //====================================================
        // Derived Image Key
        //====================================================

        const derivedImageKey =
            basketItem.basketContentType === "DERIVED_IMAGE"

                ?

                "derived-" + basketItem.basketId

                :

                null;


        //====================================================
        // Content
        //====================================================

        return {

            //================================================
            // Basket
            //================================================

            basketId:
                basketItem.basketId,

            basketContentType:
                basketItem.basketContentType || null,


            //================================================
            // Source Document
            //================================================

            documentId:
                basketItem.documentId ?? null,

            documentSharepointId:
                basketItem.documentSharepointId ?? null,

            documentTitle:
                basketItem.documentTitle || null,


            //================================================
            // Source Page
            //================================================

            documentSnippetId:
                basketItem.documentSnippetId ?? null,

            pageNumber:
                basketItem.pageNumber ?? null,


            //================================================
            // Source Section
            //================================================

            documentSnippetSectionId:
                basketItem.documentSnippetSectionId ?? null,

            sectionSequence:
                basketItem.sectionSequence ?? null,

            sectionType:
                basketItem.sectionType || null,


            //================================================
            // Content
            //================================================

            selectedText:
                basketItem.selectedText ?? null,

            contentType:
                basketItem.contentType || null,


            //================================================
            // Selection
            //================================================

            selectionType:
                basketItem.selectionType || null,

            source:
                basketItem.source || null,


            //================================================
            // Source Geometry
            //================================================

            x:
                basketItem.x ?? null,

            y:
                basketItem.y ?? null,

            width:
                basketItem.width ?? null,

            height:
                basketItem.height ?? null,


            //================================================
            // Derived Image
            //================================================

            derivedImageKey:
                derivedImageKey,

            layoutMode:
                basketItem.layoutMode || null,


            //================================================
            // Derived Image Provenance
            //================================================

            sources:

                Array.isArray(
                    basketItem.sources
                )

                    ?

                    basketItem.sources.map(

                        source =>

                        ({

                            basketId:
                                source?.basketId ?? null,

                            documentId:
                                source?.documentId ?? null,

                            documentSharepointId:
                                source?.documentSharepointId ?? null,

                            documentTitle:
                                source?.documentTitle ?? null,

                            documentSnippetId:
                                source?.documentSnippetId ?? null,

                            documentSnippetSectionId:
                                source?.documentSnippetSectionId ?? null,

                            pageNumber:
                                source?.pageNumber ?? null,

                            sectionSequence:
                                source?.sectionSequence ?? null,

                            sectionType:
                                source?.sectionType ?? null,

                            contentType:
                                source?.contentType ?? null,

                            x:
                                source?.x ?? null,

                            y:
                                source?.y ?? null,

                            width:
                                source?.width ?? null,

                            height:
                                source?.height ?? null

                        })

                    )

                    :

                    []

        };

    };


    //========================================================
    // Build Cell Content
    //
    // Combines the authoritative source definition with
    // the editable snapshot stored in the document layout.
    //
    // Never copies a browser File into the JSON definition.
    //========================================================

    const buildCellContent = (cellContent) =>
    {

        if (
            !cellContent
        )
        {
            return null;
        }


        //====================================================
        // Source Content
        //====================================================

        const basketItem =
            findBasketItem(
                cellContent.basketId
            );

        const sourceContent =
            buildSourceContent(
                basketItem
            );


        //====================================================
        // Missing Source
        //
        // Do not silently generate an incomplete document
        // if a referenced basket item has disappeared.
        //====================================================

        if (

            cellContent.basketId != null &&
            !basketItem

        )
        {

            throw new Error(

                "A document cell references a basket item " +
                "that is no longer available: " +
                cellContent.basketId

            );

        }


        //====================================================
        // Edited Source Text
        //
        // Keep source contentType and provenance intact.
        //
        // The edited text/html are separate snapshot fields.
        //====================================================

        if (
            basketItem
        )
        {

            return {

                ...sourceContent,

                title:
                    cellContent.title ?? null,

                text:
                    cellContent.text ?? null,

                html:
                    cellContent.html ?? null,

                caption:
                    cellContent.caption ?? null,

                formatting:
                    cellContent.formatting ?? null,

                edited:
                    cellContent.text !== undefined ||
                    cellContent.html !== undefined

            };

        }


        //====================================================
        // Authored Text
        //====================================================

        if (
            cellContent.contentType === "TEXT"
        )
        {

            return {

                basketId:
                    null,

                basketContentType:
                    null,

                contentType:
                    "TEXT",

                source:
                    "DOCUMENT_BUILDER",

                title:
                    cellContent.title || "",

                text:
                    cellContent.text || "",

                html:
                    cellContent.html || "",

                formatting:
                    cellContent.formatting || null,

                selectedText:
                    null

            };

        }


        //====================================================
        // Uploaded Image
        //
        // uploadKey links the JSON to its multipart file.
        //====================================================

        if (
            cellContent.contentType === "UPLOADED_IMAGE"
        )
        {

            if (

                !cellContent.uploadKey ||
                !cellContent.imageFile

            )
            {

                throw new Error(

                    "An uploaded image is missing its " +
                    "file or upload key."

                );

            }

            return {

                basketId:
                    null,

                basketContentType:
                    null,

                contentType:
                    "UPLOADED_IMAGE",

                source:
                    "DOCUMENT_BUILDER",

                uploadKey:
                    cellContent.uploadKey,

                fileName:
                    cellContent.fileName ||
                    cellContent.imageFile.name,

                caption:
                    cellContent.caption || "",

                formatting:
                    cellContent.formatting || null

            };

        }


        //====================================================
        // Unknown Content
        //====================================================

        throw new Error(

            "Unsupported document cell content type: " +
            String(cellContent.contentType)

        );

    };


    //========================================================
    // Build Document Request
    //
    // The document layout controls page, row and cell order.
    //========================================================

    const buildDocumentRequest = () =>
    {

        const pages =
            Array.isArray(
                documentLayout?.pages
            )

                ?

                documentLayout.pages

                :

                [];


        return {

            title:
                documentLayout?.title ||
                "Untitled Document",

            outputType:
                "PDF",

            pages:

                pages.map(

                    (page, pageIndex) =>

                    ({

                        pageNumber:
                            pageIndex + 1,

                        orientation:
                            page?.orientation ||
                            "PORTRAIT",

                        rows:

                            (

                                Array.isArray(
                                    page?.rows
                                )

                                    ?

                                    page.rows

                                    :

                                    []

                            ).map(

                                (row, rowIndex) =>

                                ({

                                    sequence:
                                        rowIndex + 1,

                                    cells:

                                        (

                                            Array.isArray(
                                                row?.cells
                                            )

                                                ?

                                                row.cells

                                                :

                                                []

                                        ).map(

                                            (cell, cellIndex) =>

                                            ({

                                                sequence:
                                                    cellIndex + 1,

                                                width:
                                                    cell?.width ?? 100,

                                                content:
                                                    buildCellContent(
                                                        cell?.content
                                                    )

                                            })

                                        )

                                })

                            )

                    })

                )

        };

    };


    //========================================================
    // Add Derived Images
    //
    // Existing multipart contract:
    //
    // Field: derivedImages
    // Filename: derivedImageKey + ".png"
    //========================================================

    const addDerivedImagesToFormData = (
        formData,
        documentRequest
    ) =>
    {

        const addedDerivedImages =
            new Set();

        documentRequest?.pages?.forEach(

            page =>
            {

                page?.rows?.forEach(

                    row =>
                    {

                        row?.cells?.forEach(

                            cell =>
                            {

                                const content =
                                    cell?.content;

                                if (

                                    !content ||

                                    content.basketContentType !==
                                        "DERIVED_IMAGE" ||

                                    !content.derivedImageKey

                                )
                                {
                                    return;
                                }


                                //================================
                                // Duplicate Protection
                                //================================

                                if (

                                    addedDerivedImages.has(
                                        content.derivedImageKey
                                    )

                                )
                                {
                                    return;
                                }


                                //================================
                                // Basket Item
                                //================================

                                const basketItem =
                                    findBasketItem(
                                        content.basketId
                                    );

                                if (
                                    !basketItem?.blob
                                )
                                {

                                    throw new Error(

                                        "Derived image Blob is missing: " +
                                        content.basketId

                                    );

                                }


                                //================================
                                // Attach PNG
                                //================================

                                formData.append(

                                    "derivedImages",

                                    basketItem.blob,

                                    content.derivedImageKey +
                                    ".png"

                                );

                                addedDerivedImages.add(
                                    content.derivedImageKey
                                );

                            }

                        );

                    }

                );

            }

        );

    };


    //========================================================
    // Add Uploaded Images
    //
    // New multipart contract for the Java backend:
    //
    // Field: uploadedImages
    // Filename: uploadKey
    //
    // The original fileName is retained in document JSON.
    //
    // The backend must use uploadKey to match the file to
    // its corresponding content definition.
    //========================================================

    const addUploadedImagesToFormData = (
        formData
    ) =>
    {

        const addedUploadedImages =
            new Set();

        documentLayout?.pages?.forEach(

            page =>
            {

                page?.rows?.forEach(

                    row =>
                    {

                        row?.cells?.forEach(

                            cell =>
                            {

                                const content =
                                    cell?.content;

                                if (

                                    content?.contentType !==
                                        "UPLOADED_IMAGE"

                                )
                                {
                                    return;
                                }


                                //================================
                                // Validate
                                //================================

                                if (

                                    !content.uploadKey ||
                                    !content.imageFile

                                )
                                {

                                    throw new Error(

                                        "An uploaded image is missing " +
                                        "its file or upload key."

                                    );

                                }


                                //================================
                                // Duplicate Protection
                                //================================

                                if (

                                    addedUploadedImages.has(
                                        content.uploadKey
                                    )

                                )
                                {
                                    return;
                                }


                                //================================
                                // Attach File
                                //================================

                                formData.append(

                                    "uploadedImages",

                                    content.imageFile,

                                    content.uploadKey

                                );

                                addedUploadedImages.add(
                                    content.uploadKey
                                );

                            }

                        );

                    }

                );

            }

        );

    };


    //========================================================
    // Create Document
    //========================================================

    const createDocument = async () =>
    {

        if (
            creatingDocument
        )
        {
            return;
        }

        setDocumentCreationError(
            null
        );

        setCreatingDocument(
            true
        );

        try
        {

            //================================================
            // Build Request
            //================================================

            const documentRequest =
                buildDocumentRequest();


            //================================================
            // Form Data
            //================================================

            const formData =
                new FormData();


            //================================================
            // Document JSON
            //================================================

            formData.append(

                "document",

                new Blob(

                    [
                        JSON.stringify(
                            documentRequest
                        )
                    ],

                    {
                        type:
                            "application/json"
                    }

                )

            );


            //================================================
            // Derived Images
            //================================================

            addDerivedImagesToFormData(

                formData,

                documentRequest

            );


            //================================================
            // Uploaded Images
            //================================================

            addUploadedImagesToFormData(
                formData
            );


            //================================================
            // Endpoint
            //
            // Do not manually set Content-Type.
            // The browser generates the multipart boundary.
            //================================================

            const response =
                await fetch(

                    "/resolutionsoftwareplatform/snippet-document-builder/create",

                    {

                        method:
                            "POST",

                        body:
                            formData,

                        credentials:
                            "include",

                        headers:
                        {

                            "Authorization":
                                localStorage.getItem(
                                    JWT
                                )

                        }

                    }

                );


            //================================================
            // HTTP Error
            //================================================

            if (
                !response.ok
            )
            {

                throw new Error(

                    "Document creation failed. HTTP " +
                    response.status

                );

            }


            //================================================
            // PDF
            //================================================

            const pdfBlob =
                await response.blob();

            const pdfUrl =
                window.URL.createObjectURL(
                    pdfBlob
                );


            //================================================
            // Open Generated Document
            //================================================

            window.open(

                pdfUrl,

                "_blank",

                "noopener,noreferrer"

            );


            //================================================
            // Revoke Browser URL
            //================================================

            window.setTimeout(

                () =>
                {

                    window.URL.revokeObjectURL(
                        pdfUrl
                    );

                },

                60000

            );

        }
        catch (error)
        {

            console.error(

                "Unable to create document.",

                error

            );

            setDocumentCreationError(

                error?.message ||
                "Unable to create document."

            );

        }
        finally
        {

            setCreatingDocument(
                false
            );

        }

    };


    //========================================================
    // Render
    //========================================================

    return (

        <div style={styles.container}>

            <Header

                transitionHide={true}

            />

            <LeftMenu />


            {/*============================================
                Toolbar
            ============================================*/}

            <div style={styles.toolbar}>

                <div style={styles.toolbarLeft}>

                    <div style={styles.eyebrow}>

                        DOCUMENT BUILDER

                    </div>

                    <input

                        type="text"

                        value={
                            documentLayout.title
                        }

                        onChange={
                            event =>
                            {

                                setDocumentTitle(
                                    event.target.value
                                );

                            }
                        }

                        style={styles.titleInput}

                    />

                </div>


                <div style={styles.toolbarRight}>

                    {/*========================================
                        Creation Status
                    ========================================*/}

                    {
                        creatingDocument &&
                        (

                            <div style={styles.creatingStatus}>

                                Creating document...

                            </div>

                        )
                    }


                    {/*========================================
                        Creation Error
                    ========================================*/}

                    {
                        documentCreationError &&
                        (

                            <div

                                style={styles.creationError}

                                title={
                                    documentCreationError
                                }

                            >

                                {documentCreationError}

                            </div>

                        )
                    }


                    <div style={styles.basketSummary}>

                        <span style={styles.basketCount}>

                            {basketItems.length}

                        </span>

                        <span>

                            basket items

                        </span>

                    </div>


                    {
                        onClose &&
                        (

                            <button

                                type="button"

                                style={styles.closeButton}

                                onClick={
                                    onClose
                                }

                            >

                                Back to Search

                            </button>

                        )
                    }

                </div>

            </div>


            {/*============================================
                Workspace
            ============================================*/}

            <div style={styles.workspace}>

                <div style={styles.canvasArea}>

                    <DocumentCanvas

                        documentLayout={
                            documentLayout
                        }

                        basketItems={
                            basketItems
                        }

                        onAddPage={
                            addPage
                        }

                        onDeletePage={
                            deletePage
                        }

                        onAddRow={
                            addRow
                        }

                        onDeleteRow={
                            deleteRow
                        }

                        onMoveRow={
                            moveRow
                        }

                        onChangeRowLayout={
                            changeRowLayout
                        }

                        onSetCellContent={
                            setCellContent
                        }

                        onClearCellContent={
                            clearCellContent
                        }

                        onUpdateCellContent={
                            updateCellContent
                        }

                        onSetPageOrientation={
                            setPageOrientation
                        }

                        onCreateDocument={
                            createDocument
                        }

                    />

                </div>


                <DocumentBasket

                    basketItems={
                        basketItems
                    }

                    basketUsage={
                        basketUsage
                    }

                    onAddMergedImage={
                        addMergedImageToBasket
                    }

                />

            </div>

        </div>

    );

}


//========================================================
// Styles
//========================================================

const styles =
{

    container:
    {

        width:
            "100%",

        height:
            "100vh",

        display:
            "flex",

        flexDirection:
            "column",

        background:
            "#e9eef2",

        overflow:
            "hidden"

    },


    //========================================================
    // Toolbar
    //========================================================

    toolbar:
    {

        height:
            78,

        minHeight:
            78,

        marginLeft:
            60,

        padding:
            "0 28px",

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "space-between",

        boxSizing:
            "border-box",

        background:
            "#ffffff",

        borderBottom:
            "1px solid rgba(38,55,70,0.10)",

        boxShadow:
            "0 3px 12px rgba(20,35,50,0.05)",

        zIndex:
            10

    },

    toolbarLeft:
    {

        minWidth:
            0

    },

    eyebrow:
    {

        marginBottom:
            4,

        fontSize:
            9,

        fontWeight:
            700,

        letterSpacing:
            1.4,

        color:
            OMNISCAN_BLUE

    },

    titleInput:
    {

        width:
            420,

        maxWidth:
            "45vw",

        padding:
            0,

        border:
            "none",

        outline:
            "none",

        background:
            "transparent",

        color:
            "#263746",

        fontSize:
            18,

        fontWeight:
            600

    },

    toolbarRight:
    {

        display:
            "flex",

        alignItems:
            "center",

        gap:
            18

    },


    //========================================================
    // Document Creation Status
    //========================================================

    creatingStatus:
    {

        color:
            OMNISCAN_BLUE,

        fontSize:
            10,

        fontWeight:
            600

    },

    creationError:
    {

        maxWidth:
            180,

        overflow:
            "hidden",

        textOverflow:
            "ellipsis",

        whiteSpace:
            "nowrap",

        color:
            "#b44343",

        fontSize:
            10,

        fontWeight:
            600

    },


    //========================================================
    // Basket Summary
    //========================================================

    basketSummary:
    {

        display:
            "flex",

        alignItems:
            "center",

        gap:
            7,

        color:
            "rgba(38,55,70,0.55)",

        fontSize:
            11,

        fontWeight:
            500

    },

    basketCount:
    {

        minWidth:
            24,

        height:
            24,

        padding:
            "0 6px",

        display:
            "inline-flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        boxSizing:
            "border-box",

        borderRadius:
            4,

        background:
            "rgba(32,175,232,0.10)",

        color:
            OMNISCAN_BLUE,

        fontWeight:
            700

    },

    closeButton:
    {

        height:
            38,

        padding:
            "0 16px",

        border:
            "1px solid rgba(38,55,70,0.14)",

        borderRadius:
            6,

        background:
            "#f4f7f9",

        color:
            "#263746",

        fontSize:
            12,

        fontWeight:
            600,

        cursor:
            "pointer"

    },


    //========================================================
    // Workspace
    //========================================================

    workspace:
    {

        flex:
            1,

        minHeight:
            0,

        display:
            "flex",

        overflow:
            "hidden",

        width:
            "calc(100% - 380px)"

    },

    canvasArea:
    {

        flex:
            1,

        minWidth:
            0,

        minHeight:
            0,

        overflow:
            "hidden"

    }

};